import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import React, { useCallback } from 'react'
import { Flex, Skeleton } from 'antd'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'
import { PortaledBreadcrumb } from '../components/portaled-breadcrumb'
import { ChatFilters } from '../site/api'
import { useSite } from '../site/hooks'
import { SiteHeader } from '../site/components/site-header'
import { SiteAttention } from '../site/components/site-attention'
import { SiteInbox } from '../site/components/site-inbox'
import { SiteStats, SiteTodos } from '../site/components/site-overview'

// A website opens on its chats, like a campaign opens on its candidates. The rest is in the settings.
export const Route = createFileRoute('/_auth/_main/websites/$siteId/')({
    validateSearch: z.object({
        outcome: z.string().optional(),
        search: z.string().optional(),
        from: z.string().optional(),
        to: z.string().optional(),
        page: z.number().optional(),
        chat: z.string().optional(),
        // The period of the figures and the unanswered questions above the chats.
        days: z.union([z.literal(7), z.literal(30)]).optional(),
        // The unanswered questions as a queue instead of the chats, and the one being answered.
        view: z.literal('questions').optional(),
        question: z.string().optional(),
    }),
    component: Chats,
})

function Chats() {
    const { siteId } = Route.useParams()
    const { t } = useTranslation()
    const { data: site } = useSite(siteId)
    const { chat, days = 7, view, question, ...filters } = Route.useSearch()
    const navigate = useNavigate({ from: Route.fullPath })

    const onFiltersChange = useCallback(
        (next: ChatFilters) =>
            navigate({
                search: (current) => ({
                    ...next,
                    days: current.days,
                }),
                replace: true,
            }),
        [navigate]
    )

    const onDaysChange = useCallback(
        (next: 7 | 30) =>
            navigate({
                search: (current) => ({
                    ...current,
                    days: next,
                }),
                replace: true,
            }),
        [navigate]
    )

    const onShowQuestions = useCallback(
        () =>
            navigate({
                search: (current) => ({
                    days: current.days,
                    search: current.search,
                    view: 'questions' as const,
                }),
                replace: true,
            }),
        [navigate]
    )

    // Searches the chats or the questions, whichever shows, from the first page and question.
    const onSearchChange = useCallback(
        (next?: string) =>
            navigate({
                search: (current) => ({
                    ...current,
                    search: next,
                    page: undefined,
                    question: undefined,
                }),
                replace: true,
            }),
        [navigate]
    )

    const onQuestionChange = useCallback(
        (next?: string) =>
            navigate({
                search: (current) => ({
                    ...current,
                    question: next,
                }),
                replace: true,
            }),
        [navigate]
    )

    const onChatChange = useCallback(
        (chatId: string) =>
            navigate({
                search: (current) => ({
                    ...current,
                    chat: chatId,
                }),
                replace: true,
            }),
        [navigate]
    )

    if (!site) {
        return <Skeleton active />
    }

    return (
        <>
            <PortaledBreadcrumb
                items={[
                    {
                        title: <Link to="/websites">{t('Websites')}</Link>,
                    },
                    {
                        title: site.name,
                    },
                ]}
            />
            <SiteHeader site={site} />
            <Flex
                vertical
                gap="middle"
            >
                <SiteTodos siteId={siteId} />
                {view !== 'questions' && (
                    <SiteAttention
                        siteId={siteId}
                        days={days}
                        onAnswer={onShowQuestions}
                    />
                )}
                <SiteStats
                    siteId={siteId}
                    days={days}
                    onDaysChange={onDaysChange}
                />
                <SiteInbox
                    siteId={siteId}
                    filters={filters}
                    onFiltersChange={onFiltersChange}
                    onSearchChange={onSearchChange}
                    chatId={chat}
                    onChatChange={onChatChange}
                    questions={view === 'questions'}
                    onShowQuestions={onShowQuestions}
                    question={question}
                    onQuestionChange={onQuestionChange}
                    days={days}
                    hasVacancies={(site.vacanciesCount ?? 0) > 0}
                />
            </Flex>
        </>
    )
}
